import React, { useState, useEffect, useCallback, useRef } from 'react';
import Grid from '@mui/material/Grid2';

import { SmartHash } from "@models";
import { DATA_TYPE_INFO } from "@dndModels/Constants";

import API from "@utilities/smart_api";
import { makeStyles } from "@utilities/theme_utilities";

import LoadingBox from "@components/input/LoadingBox";
import { DataType, isDataType } from '@dndModels/Types';
import ProgressBar from '@components/input/ProgressBar';
import { useApiLoadStatus } from '@utilities/useApiLoadStatus';
import { RenderDataTypeLoadStatus } from '@components/display/ApiLoadStatusOverlay';
import FlexBox from '@components/layout/FlexBox';
import ModalOrDrawer from '@components/display/ModalOrDrawer';
import DandDButtonBox from '@dndComponents/DandDButtonBoxSimple';

const data_type_map: { [data_type: string]: string } = {}; 
const ignore_us: DataType[] = [];
const use_us: DataType[] = ["floating_feature","special_feature"];
if (use_us.length > 0) {
  DATA_TYPE_INFO.filter(o => !o.player_object && use_us.includes(o.data_type)).forEach(o => {
    data_type_map[o.data_type] = o.plural;
  }); 
} else if (ignore_us.length > 0) {
  DATA_TYPE_INFO.filter(o => !o.player_object && !ignore_us.includes(o.data_type)).forEach(o => {
    data_type_map[o.data_type] = o.plural;
  }); 
} else {
  DATA_TYPE_INFO.filter(o => !o.player_object).forEach(o => {
    data_type_map[o.data_type] = o.plural;
  }); 
}
const data_types = Object.keys(data_type_map) as DataType[];

// const delay = (milliseconds: number) => new Promise(res => setTimeout(res, milliseconds));

const api = API.getInstance();

export default function AdminOnly() {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loading_message, setLoadingMessage] = useState("Loading");
  const [updating_message, setUpdatingMessage] = useState("");
  const [percentage, setPercentage] = useState(0);
  const [updated, setUpdated] = useState<string[]>([]);
  const [processing, setProcessing] = useState(false);
  const [updating, setUpdating] = useState<DataType | "">("");
  const [reload_check, setReloadCheck] = useState(false);
  const [reload_check_for_types, setReloadCheckForTypes] = useState<DataType[]>([]);
  const [loaded_data_types, setLoadedDataTypes] = useState<DataType[]>([]);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (reload_check) {
      setReloadCheck(false);
      const missing_data_types = data_types.filter(o => !loaded_data_types.includes(o));
      const new_data_types: DataType[] = [];
      for (const dt of missing_data_types) {
        if (api.isDataTypeLoaded(dt)) {
          new_data_types.push(dt);
        }
      }
      setLoadedDataTypes(prev => [...prev,...new_data_types]);
    }
  }, [reload_check, loaded_data_types]);

  useEffect(() => {
    if (reload_check_for_types.length > 0) {
      setLoadedDataTypes(prev => [...prev,...reload_check_for_types]);
      setReloadCheckForTypes([]);
    }
  }, [reload_check_for_types, loaded_data_types]);
  
  const load = useCallback( async () => {
    debugger;
    setFetched(true);
    setLoading(true);

    // const onProgress = (data_type: string, percent: number, preloading?: boolean) => {
    //   setPercentage(percent * 100);
    //   setLoadingMessage(`Preloading ${data_type_map[data_type]}`);
    // };

    const data_types = Object.keys(data_type_map) as DataType[];
    // Fetches straight off the live/standard collections, not `_light` - this page
    // exists to see the live shape of the data so it can push it back out to
    // update `_light` after a schema change.
    api.loadDataType("source_book", "priority", { live: true, onComplete: () => {
      api.queueFullLoad({ data_types });
    }});
    // const res: SmartHash = {};
    // for (const data_type of data_types) {
    //   setLoadingMessage(`Loading ${data_type_map[data_type]}`);
    //   setPercentage(0);
    //   res[data_type] = await api.loadDataType(data_type, "priority", { live: true, onProgress });
    // }
    // for (const data_type of data_types) {
    //   setLoadingMessage(`Loading ${data_type_map[data_type]}`);
    //   setPercentage(0);
    //   const objects = res[data_type];
    //   for (let i = 0; i < objects.length; ++i) {
    //     const obj = objects[i];
    //     await api.loadObject(obj);
    //     setPercentage((i / objects.length) * 100);
    //   }
    // }
    // // console.log(res.source_book.find(o => o.name === "Pointy Hat"));
    // setHashNew(res);
    setLoading(false);
  }, []);

  const dataTypeLoaded = (props: any) => {
    console.log(props);
    if (props && props.value && isDataType(props.value)) {
      setReloadCheckForTypes(prev => [...prev, props.value]);
    } else {
      setReloadCheck(true);
    }
  }

  useEffect(() => {
    console.log("Getting Ready");
    api.subscription_holder.subscribe("Data Type Loaded", mounted, dataTypeLoaded);
  }, []);

  useEffect(() => {
    if(!fetched) {
      load();
    }
  }, [fetched, load]);

  const updateDataType = async (data_type: DataType) => {
    const objects = api.getLiveDataTypeFromHash(data_type);
    if (objects && objects.length > 0 && !updated.includes(data_type)) {
      console.log(data_type);
      setUpdating(data_type);
      setPercentage(0);
      for (let i = 0; i < objects.length; ++i) {
        const obj = objects[i];
        setUpdatingMessage(`Updating ${obj.name} (${obj._id})`)
        await api.upsertLight(data_type, obj, true, true); // Currently I'm having it delete all the light stuff and then save new ones.
        // await delay(1000);
        setPercentage(((i + 1) / objects.length) * 100);
      }
    }
    setUpdated(prev => [...prev, data_type]);
  }

  // const renderUpdateButton = (data_type: DataType, width: 3 | 6 | 12 = 3) => {
  //   if (!updated.includes(data_type)) {
  //     if (!loaded_data_types.includes(data_type)) {
  //       return (
  //         <Grid key={data_type} size={width}>
  //           <ProgressBar
  //             message={ `Loading ${data_type_map[data_type]}` }
  //             value={0}
  //           />
  //         </Grid>
  //       );
  //     } else if (updating === data_type) {
  //       return (
  //         <Grid key={data_type} size={width}>
  //           <ProgressBar
  //             message={ `Updating ${data_type_map[data_type]}` }
  //             value={percentage}
  //           />
  //         </Grid>
  //       );
  //     } else {
  //       const objects = api.getLiveDataTypeFromHash(data_type);

  //       return (
  //         <Grid key={data_type} size={width}>
  //           <ButtonBox 
  //             name={`${data_type_map[data_type]} (${objects ? objects.length : "Not Loaded"})`} 
  //             disabled={processing}
  //             onClick={ async () => {
  //               // debugger;
  //               setProcessing(true);
  //               await updateDataType(data_type);
  //               setProcessing(false);
  //             }} 
  //           />
  //         </Grid>
  //       );
  //     }
  //   }
  //   return null;
  // }
  
  if (!fetched || loading) {
    return (<div className="centered"><LoadingBox message={loading_message} percentage={percentage} size={window.innerHeight * 0.5} height={window.innerHeight - 81} /></div>); // width={window.innerWidth - 17}
  } else {
    const dts = Object.keys(data_type_map) as DataType[];
    // console.log(data_type_map);
    return (
      <Grid container spacing={0} direction="row">
        <Grid className="centered" size={12}>
          <DandDButtonBox 
            name="Update All"
            disabled={processing}
            onClick={ async () => {
              setProcessing(true);
              for (const data_type of dts) {
                await updateDataType(data_type);
              }
              setProcessing(false);
              return false;
            }}
          />
        </Grid>
        { dts.includes("floating_feature") &&
          <DataTypeButton 
            data_type="floating_feature"
            updated={updated} 
            loaded_data_types={loaded_data_types} 
            updating={updating} 
            percentage={percentage} 
            processing={processing} 
            width={6} 
            onClick={ async () => {
              // debugger;
              setProcessing(true);
              await updateDataType("floating_feature");
              setProcessing(false);
            }} 
            updating_message={updating_message}
          />
        }
        { dts.includes("creature_feature") &&
          <DataTypeButton 
            data_type="creature_feature"
            updated={updated} 
            loaded_data_types={loaded_data_types} 
            updating={updating} 
            percentage={percentage} 
            processing={processing} 
            width={6} 
            onClick={ async () => {
              // debugger;
              setProcessing(true);
              await updateDataType("creature_feature");
              setProcessing(false);
            }} 
            updating_message={updating_message}
          />
        }
        <Grid size={12}>&nbsp;</Grid>
        { dts.filter(o => o !== "floating_feature" && o !== "creature_feature").sort((a, b) => { return a.localeCompare(b); }).map(data_type => {
          return (
            <DataTypeButton 
              data_type={data_type}
              updated={updated} 
              loaded_data_types={loaded_data_types} 
              updating={updating} 
              updating_message={updating_message}
              percentage={percentage} 
              processing={processing} 
              width={6} 
              onClick={ async () => {
                // debugger;
                setProcessing(true);
                await updateDataType(data_type);
                setProcessing(false);
              }} 
            />
          );
        })}
      </Grid>
    );
  }
}

interface DataTypeButtonProps {
  data_type: DataType;
  updated: string[];
  loaded_data_types: DataType[];
  updating: DataType | "";
  updating_message: string;
  percentage: number;
  processing: boolean;
  width: 3 | 6 | 12;
  onClick: () => void;
}

function DataTypeButton({ data_type, updated, loaded_data_types, updating, updating_message, percentage, processing, width, onClick }: DataTypeButtonProps) {
  const [open, setOpen] = useState(false);
  const status = useApiLoadStatus(data_type, false, undefined, true);
  // const statuses = useApiLoadStatuses();
  
  const useRowStyles = makeStyles()((theme) => {
    return ({
      root: {
        backgroundColor: theme.palette.secondary.main,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '4px',
        marginBottom: '4px',
      },
      '&.disabled': {
        color: theme.palette.primary.contrastText,
        backgroundColor: "gray",
      },
    });
  });

  const { classes: rowClasses, cx } = useRowStyles();

  const render = () => {
    if (!updated.includes(data_type)) {
      if (!loaded_data_types.includes(data_type)) {
        return (
          <Grid key={data_type} size={width}>
            { status ? RenderDataTypeLoadStatus(status)
            :
              <ProgressBar
                message={ `Loading ${data_type_map[data_type]}` }
                value={0}
              />
            }
          </Grid>
        );
      } else if (updating === data_type) {
        return (
          <Grid key={data_type} size={width}>
            <FlexBox direction="column">
              <FlexBox>
                <ProgressBar
                  message={ `Updating ${data_type_map[data_type]}` }
                  value={percentage}
                  horizontal
                />
              </FlexBox>
              <FlexBox>{ updating_message }</FlexBox>
            </FlexBox>
          </Grid>
        );
      } else {
        const objects = api.getLiveDataTypeFromHash(data_type);

        return (
          <Grid key={data_type} size={width} className="padded">
            <FlexBox direction="row" className={cx(rowClasses.root, processing ? "disabled" : "")}>
              <FlexBox className="clickable centered"
                onClick={() => { 
                  if (!processing) {
                    onClick();
                  }
                }}>
                {data_type_map[data_type]} ({objects ? objects.length : "Not Loaded"})
              </FlexBox>
              <FlexBox xs_expand={0}>
                <DandDButtonBox name="Display" width={100}
                  onClick={() => setOpen(true)} 
                />
              </FlexBox>
            </FlexBox>
            <ModalOrDrawer
              open={open}
              onClose={() => setOpen(false) }
            >
              <FlexBox direction="column">
                <FlexBox className="header centered">{ data_type_map[data_type] }</FlexBox>
                { objects.map(o => {
                  return (
                    <FlexBox key={o._id} className="padded">
                      <FlexBox className={cx(rowClasses.root, processing ? "disabled" : "")}>
                        { o.name }
                      </FlexBox>
                    </FlexBox>
                  );
                })}
              </FlexBox>
            </ModalOrDrawer>
          </Grid>
        );
      }
    }
  }
  return <>{ render() }</>;
}
